import { IProviderRoutes } from "../interfaces/provider";
import Provider from "./new/provider";
import ListView from "../models/listViews/listView";
import { Constructor } from "../../global/constructor";
import { Route } from "../enums/route";
import {
  IObjectModelEntriesResponse,
  IObjectModelsSearchResponse,
  IServerData,
  IListViewResponse
} from "../interfaces/serverInterfaces";
import { HTTPMethod } from "./new/httpMethod";
import { RouteHelper } from "../helpers/routeHelper";

interface IListViewProviderRoutes {
  create: string;
  entries: string;
  fetch: string;
  publish: Route;
  publishingDependencies: Route;
  clone: Route;
}

export default class ListViewProvider<
  T extends ListView = ListView
> extends Provider<T> {
  routes: IProviderRoutes & IListViewProviderRoutes;

  constructor(
    protected modelType: Constructor<T>,
    public modelRoot: string,
    public collectionRoot: string
  ) {
    super(modelType, modelRoot, collectionRoot);
    let newRoutes: IListViewProviderRoutes = {
      create: RouteHelper.path({ route: Route.listViews }),
      entries: RouteHelper.path({ route: Route.entriesListView }),
      fetch: RouteHelper.path({ route: Route.listView }),
      publish: Route.publishListView,
      publishingDependencies: Route.listViewPublishingDependencies,
      clone: Route.listViewClone
    };
    Object.assign(this.routes, newRoutes);
  }

  create({
    organizationId,
    folderIdTree,
    listView
  }: {
    organizationId: string;
    folderIdTree: string;
    listView: object;
  }): Promise<IServerData<IObjectModelsSearchResponse>> {
    const json = {
      organization_id: organizationId,
      folder_id_tree: folderIdTree,
      list_view: listView
    };
    return new Promise<IServerData<IObjectModelsSearchResponse>>((resolve, reject) => {
      this.serverAction<IObjectModelsSearchResponse>({
        endpoint: this.routes.create,
        method: HTTPMethod.POST,
        json: json
      }).then(resolve);
    });
  }

  fetch(id: string, context: string = null): Promise<IServerData<IListViewResponse>> {
    return new Promise<IServerData<IListViewResponse>>((resolve, reject) => {
      this.serverAction<IListViewResponse>({
        endpoint: this.replace(this.routes.fetch, { id: id }),
        method: HTTPMethod.GET,
        json: {id, context},
      }).then(resolve);
    });
  }

  entries(
    listView: ListView,
    json: object = {}
  ): Promise<IServerData<IObjectModelEntriesResponse>> {
    json = {
      filter: {
        ...listView.filterQuery,
      },
      ...listView.filterQuery,
      ...json,
    };

    return new Promise<IServerData<IObjectModelEntriesResponse>>((resolve, reject) => {
      this.serverAction<IObjectModelEntriesResponse>({
        endpoint: this.replace(this.routes.entries, {
          // use list_view_id in case listView is a published view
          id: listView['list_view_id'] || listView.id,
        }),
        method: HTTPMethod.GET,
        json: json
      }).then(resolve);
    });
  }

  publish(listView: ListView): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: RouteHelper.path({
          route: this.routes.publish,
          modelOrId: listView
        }),
        method: HTTPMethod.POST
      }).then(resolve);
    });
  }

  clone({ organizationId, folderIdTree, listViewId, title, reference_name }) {
    let json = {
      title: title,
      reference_name: reference_name,
      list_view_id: listViewId,
      organization_id: organizationId,
      folder_id_tree: folderIdTree
    };
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: RouteHelper.path({
          route: this.routes.clone,
          modelOrId: listViewId
        }),
        method: HTTPMethod.POST,
        json: json
      }).then(resolve);
    });
  }

  publishingDependencies(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction({
        endpoint: RouteHelper.path({
          route: this.routes.publishingDependencies,
          modelOrId: obj
        }),
        method: HTTPMethod.GET
      }).then(resolve);
    });
  }
}
